import { useCallback, useState, useEffect } from 'react';
import { Grid, Switch, Button } from '@mui/material';
import PropTypes from 'prop-types';
import { NOTICES } from '../../../../reusable-components/datagrid/systemNoticesColumns';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import Iconify from '../../../../reusable-components/iconify';
import SystemNoticeForm from './SystemNoticeForm';
import EditIconButton from '../../../../reusable-components/basic-buttons/EditIconButton';
import axiosInstance from '../../../../utils/axios';
import { useScopeCheck } from '../../../../reusable-components/scopes';
import InfoIconButton from '../../../../reusable-components/basic-buttons/InfoIconButton';
import { objectToFormData } from '../../../../api/utils';
import { ConfirmDialog } from '../../../../reusable-components/confirm-dialog';

SystemNoticesView.propTypes = {
  viewMain: PropTypes.bool
}

export default function SystemNoticesView({ viewMain }) {
  const { enqueueSnackbar } = useSnackbar();
  const isEditSystemNotices = useScopeCheck(['Edit-SystemNotices'], false, true);
  const isSuperAdmin= useScopeCheck(['Super-Admin'], false, true);

  const [data, setData] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editNotice, setEditNotice] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axiosInstance.get(`SystemNotice/SystemNotices?viewMain=${viewMain}`)
      .then(res => setData(res.data ?? []))
      .catch(() => enqueueSnackbar('Error fetching System Notices', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const tableToolbar = useCallback(() => <Grid container spacing={1}>
    {isEditSystemNotices && <Grid item>
      <Button
        variant="contained"
        color="secondary"
        size="small"
        onClick={() => { setEditNotice(null); setOpenForm(true); }}
        startIcon={<Iconify icon="eva:plus-outline" />}
      >
        Add new notice
      </Button>     
    </Grid>}
    {isEditSystemNotices && viewMain && isSuperAdmin && <Grid item>
      <ConfirmDialog
        icon="add"
        content={`Are you sure you want to add a system update notice?\n This will add a notice with the text 'Update scheduled in 5-10 minutes. Please ensure your work is saved.' and will remove this notice after the publish automatically`}
        actionButton="Add system update notice"
        title="Add system update notice"
        action={() => {
          axiosInstance.post('SystemNotice/SystemNoticeForm', objectToFormData({
            title: "System Update",
            viewMain,
            messages: [{ message: "Update scheduled in 5-10 minutes. Please ensure your work is saved." }]
          })).then((res) => {
            if (res.data) {
              setData(data => [res.data, ...data]);
              enqueueSnackbar(`System notice added`, { variant: "success" })
            }
          }).catch(() => enqueueSnackbar(`Failed to add notice`, { variant: "error" }))
        }}
        button="contained"
        variant="contained"
        color="secondary"
        size="small"
      >
        Add system update notice
      </ConfirmDialog>
    </Grid>}
    <Grid item>
      <Button
        variant="outlined"
        color="secondary"
        size="small"
        onClick={() => window.open(`/Api/SystemNotice/DownloadSystemNotices${viewMain ? "?viewMain=true" : ""}`, '_blank')}
        startIcon={<Iconify icon="file-icons:microsoft-excel" />}
      >
        Download
      </Button>
    </Grid>
  </Grid>, []);

  const ActionButtons = (params) => {
    const [saving, setSaving] = useState(false);
    const editClick = () => { setOpenForm(true); setEditNotice(params.row); };

    return <>
      {isEditSystemNotices ? <EditIconButton onClick={editClick} /> : <InfoIconButton onClick={editClick} />} 
      {isEditSystemNotices && <Switch
        color="info"
        checked={!params.row.complete}
        disabled={saving}
        onChange={() => {
          setSaving(true);
          axiosInstance.post(`SystemNotice/SystemNoticeCompleteToggle?id=${params.row.id}&complete=${!params.row.complete}`)
            .then((res) => {
              enqueueSnackbar('Notice updated successfully', { variant: 'success' });
              setData(data => data.map(val => val.id === res.data.id ? { ...val, complete: res.data.endDate !== null, endDate: res.data.endDate } : val))
            })
            .catch(() => enqueueSnackbar('Notice update failed', { variant: 'error' }))
            .finally(() => setSaving(false));
        }}
      />}
    </>
};

  const actions = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    width: isEditSystemNotices ? 120 : 70,
    sortable: isEditSystemNotices,
    renderCell: (params) => ActionButtons(params),
    valueGetter: (params) => params.row.complete
  };

  return <>
    <CustomDataGrid
      gridId={`${viewMain ? "super-" : ""}admin-system-notices-view`}
      boxSX={{ height: 'calc(100vh - 225px)' }}
      data={data}
      gridColumns={[...NOTICES, actions]}
      sort={[{ field: 'dateAdded', sort: 'desc' }]}
      disableSelectionOnClick
      disableCellHighlight
      CustomLeftToolbar={tableToolbar}
      loading={loading}
    />
    {openForm && <SystemNoticeForm close={() => { setOpenForm(false); setEditNotice(null); }} setSystemNotices={setData} editNotice={editNotice} viewMain={viewMain} viewOnly={!isEditSystemNotices} />}
  </>;
}
